/* eslint-disable no-param-reassign */
import {
  find, maxBy, filter, findLastIndex, sortBy, includes, lowerCase, forEach, get, map,
  compact, uniq, findIndex, sumBy, without, each,
} from 'lodash';
import Vue from 'vue';
import { getField, updateField } from 'vuex-map-fields';
import ProductApi from '../backend/ProductApi';
import {
  assignHelper, imageNameForProduct, isRackable, isContainer, openToast,
} from './helpers';
import ConfigurationApi from '../backend/ConfigurationApi';
import {
  FILTER_FOR_TYPES, PANNEAU_BRASSAGE, BAIE, PLATEAU,
} from '../constants';
import router from '../router';

const CURRENT_CONFIG_INIT_STATE = {
  name: undefined,
  comment: undefined,
  configuration_products: [], // Rails case to send directly to the backend
  quoteMessage: undefined,
  unsaved: undefined,
};

export default {
  namespaced: true,
  state: {
    filters: {
      type: PANNEAU_BRASSAGE,
      query: undefined,
    },
    configurations: [],
    currentConfiguration: { ...CURRENT_CONFIG_INIT_STATE },
    products: {},
    productsFetched: false,
    configurationsFetched: false,
    sortedProducts: [],
    pendingAction: undefined,
  },
  mutations: {
    updateField,
    setConfigurations: assignHelper('configurations'),
    setCurrentConfiguration: (state, currentConfiguration) => {
      const newConfigProducts = currentConfiguration.configuration_products.map((cp) => ({
        ...cp, isSelected: false,
      }));
      const newConfiguration = currentConfiguration;
      newConfiguration.configuration_products = newConfigProducts;
      state.currentConfiguration = newConfiguration;
    },
    setProducts: assignHelper('products'),
    setProductsFetched: assignHelper('productsFetched'),
    setConfigurationsFetched: assignHelper('configurationsFetched'),
    setPendingAction: assignHelper('pendingAction'),
    setConfigurationUnsaved: (state, value) => {
      state.currentConfiguration.unsaved = value;
    },
    addConfigurationProducts: (state, configurationProducts) => {
      const cps = state.currentConfiguration.configuration_products;
      state.currentConfiguration.configuration_products = cps.concat(configurationProducts);
    },
    removeConfigurationProducts: (state, { product, quantityToRemove }) => {
      const cps = state.currentConfiguration.configuration_products;
      for (let i = quantityToRemove; i > 0; i -= 1) {
        const index = findLastIndex(cps, { product_id: product.id });
        cps.splice(index, 1);
      }
    },
    sortConfigurationProducts: (state) => {
      state.currentConfiguration.configuration_products = sortBy(
        state.currentConfiguration.configuration_products,
        'position',
      );
    },
    reorganizeBaie: (state) => {
      state.currentConfiguration.configuration_products.reduce((nextPosition, cp) => {
        const currentProduct = find(state.products, { id: cp.product_id });
        if (!isRackable(currentProduct)) {
          cp.position = null;
        } else {
          cp.position = nextPosition;
          nextPosition += currentProduct.taille;
        }
        return nextPosition;
      }, 1);
    },
    selectType: (state, value) => {
      state.filters.type = value;
      state.filters.query = null;
    },
    swapConfigurationElements: (state, { topElement, bottomElement }) => {
      const topElementPosition = topElement.position;
      const bottomProduct = find(state.products, { id: bottomElement.product_id });
      const newTopElementTaille = bottomProduct.taille;

      bottomElement.position = topElementPosition;
      topElement.position = topElementPosition + newTopElementTaille;
    },
    setConfigurationElementPosition: (_, { element, newPosition }) => {
      element.position = newPosition;
    },
    updateFilter(state, { filterName: filterStoreName, value }) {
      Vue.set(state.filters, filterStoreName, value);
    },
    addConfiguration(state, newConfiguration) {
      state.configurations.push(newConfiguration);
    },
    removeConfiguration(state, configurationId) {
      const index = findIndex(state.configurations, { id: configurationId });
      state.configurations.splice(index, 1);
    },
    sortProducts(state, products) {
      state.sortedProducts = sortBy(
        products,
        (product) => -filter(
          state.currentConfiguration.configuration_products,
          { product_id: product.id },
        ).length,
      );
    },
    selectConfigurationElement({ currentConfiguration }, element) {
      const selectedElement = find(
        currentConfiguration.configuration_products, { position: element.position },
      );
      selectedElement.isSelected = true;
    },
    unselectConfigurationElement({ currentConfiguration }) {
      const selectedElement = find(currentConfiguration.configuration_products, 'isSelected');
      if (selectedElement) {
        selectedElement.isSelected = false;
      }
    },
    resetProductQuantities(state) {
      each(state.products, (product) => {
        product.quantity = null;
      });
    },
  },
  actions: {
    selectType({ commit, dispatch }, value) {
      commit('selectType', value);
      dispatch('sortProducts');
    },
    fetchConfigurations({ state, commit }) {
      if (!state.configurationsFetched) {
        ConfigurationApi.list().then((response) => {
          commit('setConfigurations', response.data);
          commit('setConfigurationsFetched', true);
        });
      }
    },
    clearCurrentConfiguration({ commit }) {
      commit('setCurrentConfiguration', { ...CURRENT_CONFIG_INIT_STATE });
      commit('resetProductQuantities');
    },
    openConfiguration({ state, commit }, configurationId) {
      if (state.currentConfiguration.id === configurationId && state.currentConfiguration.unsaved) {
        return;
      }
      const configuration = find(state.configurations, { id: configurationId });
      if (configuration) {
        commit('setCurrentConfiguration', configuration);
        commit('setConfigurationUnsaved', false);
      } else {
        ConfigurationApi.get(configurationId).then((response) => {
          commit('setCurrentConfiguration', response.data);
          commit('setConfigurationUnsaved', false);
        });
      }
      commit('setConfigurationUnsaved', false);
    },
    updateFilter({ dispatch, commit }, payload) {
      commit('updateFilter', payload);
      dispatch('sortProducts');
    },
    fetchProducts({ state, commit }) {
      if (!state.productsFetched) {
        ProductApi.list().then((response) => {
          commit('setProducts', response.data);
          commit('setProductsFetched', true);
        });
      }
    },
    saveConfiguration({ state, commit, dispatch }) {
      return dispatch('authentication/fetchMe', null, { root: true })
        .then(() => {
          if (state.currentConfiguration.id) {
            return ConfigurationApi.update(state.currentConfiguration).then(() => {
              commit('setConfigurationUnsaved', false);
              commit('removeConfiguration', state.currentConfiguration.id);
              commit('addConfiguration', state.currentConfiguration);
              router.replace(`/uni-baies/configuration/${state.currentConfiguration.id}/resume`);
              openToast('Votre configuration a été sauvegardée');
            });
          }
          return ConfigurationApi.create(state.currentConfiguration).then((response) => {
            commit('setCurrentConfiguration', response.data);
            commit('setConfigurationUnsaved', false);
            commit('addConfiguration', response.data);
            router.replace(`/uni-baies/configuration/${response.data.id}/resume`);
            openToast('Votre configuration a été sauvegardée');
          });
        })
        .catch((() => {
          commit('setPendingAction', 'saveConfiguration');
          router.push(`/connexion?redirect=${router.currentRoute.path}`);
          throw new Error('User not authenticated');
        }));
    },
    askQuote({ state, commit, dispatch }) {
      return dispatch('authentication/fetchMe', null, { root: true })
        .then(() => {
          if (state.currentConfiguration.id) {
            return ConfigurationApi.askQuote(
              state.currentConfiguration.id, state.currentConfiguration.quoteMessage,
            ).then(() => {
              openToast('Votre demande de devis a été prise en compte. Nous vous recontacterons dans les plus brefs délais');
            });
          }
          return ConfigurationApi.create(state.currentConfiguration).then((response) => {
            commit('setCurrentConfiguration', response.data);
            ConfigurationApi.askQuote(
              state.currentConfiguration.id, state.currentConfiguration.quoteMessage,
            ).then(() => {
              openToast('Votre demande de devis a été prise en compte. Nous vous recontacterons dans les plus brefs délais');
            });
          });
        })
        .catch((() => {
          commit('setPendingAction', 'askQuote');
          router.push(`/connexion?redirect=${router.currentRoute.path}`);
          throw new Error('User not authenticated');
        }));
    },
    executePendingAction({ state, commit, dispatch }) {
      if (state.pendingAction) {
        dispatch(state.pendingAction);
        commit('setPendingAction', null);
      }
    },
    async printConfigurationDossierTechnique({ state }, svg) {
      const response = await ConfigurationApi.print(state.currentConfiguration, svg);
      return window.URL.createObjectURL(response.data);
    },
    async duplicateConfiguration({ commit }, { configurationId, configuration }) {
      const response = await ConfigurationApi.duplicate(configurationId, configuration);
      commit('addConfiguration', response.data);
      return response;
    },
    async deleteConfiguration({ state, commit }, configuratonId) {
      const response = await ConfigurationApi.delete(configuratonId);
      if (configuratonId === state.currentConfiguration.id) {
        commit('clearCurrentConfiguration');
      }
      commit('removeConfiguration', configuratonId);
      return response;
    },
    sortProducts({ commit, getters }) {
      commit('sortProducts', getters.filteredProducts);
    },
    selectConfigurationElement({ commit }, configurationElement) {
      commit('unselectConfigurationElement');
      commit('selectConfigurationElement', configurationElement);
    },
    moveProduct({ state, commit, getters }, direction) {
      const selectedElement = getters.selectedConfigurationProduct;
      const selectedProduct = getters.getProductById(selectedElement.product_id);
      if (selectedElement.position + direction < 1
          || selectedElement.position + selectedProduct.taille
             + direction - 1 > getters.container.taille) {
        return;
      }

      // eslint-disable-next-line camelcase
      const { configuration_products } = state.currentConfiguration;
      const newPosition = selectedElement.position + direction;

      const elementsIntersects = (a, b) => (
        a.position && b.position && (
          a.position === b.position
              || (
                a.position < b.position
                && (b.position < (a.position + a.taille))
              )
              || (
                (b.position < a.position)
                && (a.position < (b.position + b.taille))
              )
        )
      );

      const permutedConfElement = find(
        without(configuration_products, selectedElement),
        (element) => {
          const elementProduct = find(state.products, { id: element.product_id });
          const currentElementProduct = getters.getProductById(selectedElement.product_id);
          return elementsIntersects(
            { position: element.position, taille: elementProduct.taille },
            { position: newPosition, taille: currentElementProduct.taille },
          );
        },
      );

      if (permutedConfElement) {
        if (direction === -1) {
          commit('swapConfigurationElements',
            { topElement: permutedConfElement, bottomElement: selectedElement });
        } else if (direction === 1) {
          commit('swapConfigurationElements',
            { topElement: selectedElement, bottomElement: permutedConfElement });
        }
      } else {
        commit('setConfigurationElementPosition',
          { element: selectedElement, newPosition });
      }

      commit('sortConfigurationProducts');
      commit('setConfigurationUnsaved', true);
    },
    resetContainerChoice({ dispatch, getters }) {
      if (getters.container) {
        dispatch('setProductQuantity', { product: getters.container, quantity: 0 });
      }
    },
    setProductQuantity({ state, commit, getters }, { product, quantity }) {
      commit('setConfigurationUnsaved', true);
      // eslint-disable-next-line camelcase
      const { configuration_products } = state.currentConfiguration;
      const intQuantity = parseInt(quantity, 10);
      const thisProductCount = filter(configuration_products, { product_id: product.id }).length;

      const maxConfigurationsize = (getters.container && getters.container.taille) || 42;

      if (intQuantity >= 0) {
        if (intQuantity < thisProductCount) {
          const quantityToRemove = thisProductCount - intQuantity;
          commit('removeConfigurationProducts', { product, quantityToRemove });
          commit('reorganizeBaie');
        } else {
          let nextPosition = getters.nextAvailablePosition;
          const newConfigurationProducts = [];

          for (let i = thisProductCount; i < intQuantity; i += 1) {
            if (nextPosition + product.taille - 1 > maxConfigurationsize && isRackable(product)) {
              if (maxConfigurationsize - getters.configurationSize
                  >= product.taille * intQuantity) {
                commit('reorganizeBaie');
                nextPosition = getters.nextAvailablePosition;
              } else {
                openToast("Certains produits n'ont pas pu être ajoutés à la configuration. La capacité de votre baie ou coffret a été atteinte", 'is-danger');
                break;
              }
            }


            if (isRackable(product)) {
              newConfigurationProducts.push({
                product_id: product.id,
                position: nextPosition,
                isSelected: false,
              });
              nextPosition += product.taille;
            } else if (isContainer(product) && getters.containers.length) {
              openToast('Vous avez déjà choisi une baie ou un coffret', 'is-danger');
              break;
            } else {
              newConfigurationProducts.push({
                product_id: product.id,
                position: null,
                isSelected: false,
              });
            }
          }
          commit('addConfigurationProducts', newConfigurationProducts);
          if (!isRackable(product) && !(isContainer(product))) {
            openToast('Les éléments ont été ajoutés à votre configuration');
          }
        }
      }
      commit('sortConfigurationProducts');
    },
  },
  getters: {
    getField,
    sortedConfigurationProducts: (state) => sortBy(
      state.currentConfiguration.configuration_products, 'position',
    ),
    getProductById: (state) => (productId) => find(state.products, { id: productId }),
    currentProducts: ({ currentConfiguration }, { getProductById }) => map(
      currentConfiguration.configuration_products,
      (currentProduct) => getProductById(currentProduct.product_id),
    ),
    possibleOptionsForAttribute: (state, getters) => (productAttribute) => {
      const categoryProducts = getters.filterProductsByType(state.filters.type);
      return compact(
        uniq(map(categoryProducts, (product) => get(product, productAttribute))),
      ).sort((a, b) => a - b);
    },
    maxPlateauSize: (state, { currentProducts }) => {
      const maxPlateau = maxBy(filter(currentProducts, { productable_type: PLATEAU }), 'productable.profondeur');
      if (maxPlateau) {
        return parseInt(maxPlateau.productable.profondeur, 10);
      }
      return 0;
    },
    configurationSize: (state, { rackableElements }) => sumBy(rackableElements, 'taille'),
    getQuantityByProduct: (state) => (product) => filter(
      state.currentConfiguration.configuration_products,
      { product_id: product.id },
    ).length,
    filterProductsByType: (state) => (productType) => filter(
      state.products,
      { productable_type: productType },
    ),
    filteredProducts: (state, getters) => {
      let result = getters.filterProductsByType(state.filters.type);
      if (state.filters.query) {
        const lowerCasedQuery = lowerCase(state.filters.query);
        result = filter(result,
          (product) => includes(lowerCase(product.designation), lowerCasedQuery)
                       || includes(product.reference, state.filters.query)
                       || includes(product.reference.replace(/\s/g, ''), state.filters.query));
      }
      const filters = FILTER_FOR_TYPES[state.filters.type];
      if (filters) {
        forEach(filters, ({ filterStoreName, filterProductAttribute }) => {
          const filterValue = get(state.filters, filterStoreName);

          if (filterValue) {
            result = filter(result, (product) => {
              const productValueForFilter = get(product, filterProductAttribute);
              return productValueForFilter
              && (filterValue.toString() === productValueForFilter.toString());
            });
          }
        });
      }
      result.map((product) => {
        const productQuantityInConfiguration = getters.getQuantityByProduct(product);
        if (productQuantityInConfiguration && !product.quantity) {
          return Object.assign(product, { quantity: productQuantityInConfiguration });
        }
        return product;
      });
      return result;
    },
    suggestedBaie: (state, getters) => {
      if (getters.configurationSize > 42) {
        return undefined;
      }
      const baies = getters.filterProductsByType(BAIE);
      return find(
        baies,
        (baie) => baie.taille === 42,
      );
    },
    baieElements({ currentConfiguration }, { getProductById }) {
      return compact(currentConfiguration.configuration_products.map((confProduct) => {
        const product = getProductById(confProduct.product_id);
        if (isRackable(product)) {
          return {
            isSelected: confProduct.isSelected,
            position: confProduct.position,
            name: imageNameForProduct(product),
            title: product.designation,
          };
        } return '';
      }));
    },
    rackableElements: (state, { currentProducts }) => filter(currentProducts, isRackable),
    containers: (state, { currentProducts }) => filter(currentProducts, isContainer),
    container: (state, { containers }) => containers[0],
    containersSize: (state, { containers }) => sumBy(containers, 'taille'),
    isContainerAdequate: (state, { configurationSize }) => (container) => (
      container.taille >= configurationSize
    ),
    selectedConfigurationProduct({ currentConfiguration }) {
      return find(currentConfiguration.configuration_products, 'isSelected');
    },
    nextAvailablePosition({ currentConfiguration }, getters) {
      let position = 1;

      const lastConfigurationProduct = maxBy(currentConfiguration.configuration_products, 'position');
      if (lastConfigurationProduct) {
        const lastProduct = getters.getProductById(lastConfigurationProduct.product_id);
        position = lastConfigurationProduct.position + lastProduct.taille;
      }

      return position;
    },
  },
};
